import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../services/api";
import Routines from "../Routines/Routines";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./AnnaCore.css";

function AnnaCore() {
  const validTabs = ["skills", "routines", "prompt", "routine-prompt", "email-prompt"];
  const hashTab = window.location.hash.replace("#", "");
  const [tab, setTab] = useState(validTabs.includes(hashTab) ? hashTab : "skills");

  const switchTab = (t) => { setTab(t); window.location.hash = t; };

  const estimateTokens = (text) => Math.ceil((text || "").length / 4);

  // --- Core Prompt state ---
  const [corePrompt, setCorePrompt] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);
  const [promptUpdatedAt, setPromptUpdatedAt] = useState("");
  const [activeVersion, setActiveVersion] = useState(0);
  const [promptVersions, setPromptVersions] = useState([]);

  // --- Routine Prompt state ---
  const [routinePrompt, setRoutinePrompt] = useState("");
  const [routinePromptLoading, setRoutinePromptLoading] = useState(false);
  const [routinePromptSaved, setRoutinePromptSaved] = useState(false);
  const [routinePromptUpdatedAt, setRoutinePromptUpdatedAt] = useState("");
  const [routineActiveVersion, setRoutineActiveVersion] = useState(0);
  const [routinePromptVersions, setRoutinePromptVersions] = useState([]);

  // --- Email Prompt state (Gmail-triage system prompt; powers gmail_sync via Gemma) ---
  const [emailPrompt, setEmailPrompt] = useState("");
  const [emailPromptLoading, setEmailPromptLoading] = useState(false);
  const [emailPromptSaved, setEmailPromptSaved] = useState(false);
  const [emailPromptUpdatedAt, setEmailPromptUpdatedAt] = useState("");
  const [emailActiveVersion, setEmailActiveVersion] = useState(0);
  const [emailPromptVersions, setEmailPromptVersions] = useState([]);

  // --- Skills state ---
  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [editingSkillId, setEditingSkillId] = useState(null);
  const [creatingSkill, setCreatingSkill] = useState(false);
  const [deletingSkillId, setDeletingSkillId] = useState(null);
  const [skillsError, setSkillsError] = useState("");

  const fetchPrompt = useCallback(async () => {
    try {
      const data = await apiClient.get("/prompt");
      setCorePrompt(data.prompt || "");
      setPromptUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || 0);
      setPromptVersions(data.versions || []);
    } catch (err) {
      console.error("Failed to fetch prompt:", err);
    }
  }, []);

  const fetchRoutinePrompt = useCallback(async () => {
    try {
      const data = await apiClient.get("/routine-prompt");
      setRoutinePrompt(data.prompt || "");
      setRoutinePromptUpdatedAt(data.updatedAt || "");
      setRoutineActiveVersion(data.activeVersion || 0);
      setRoutinePromptVersions(data.versions || []);
    } catch (err) {
      console.error("Failed to fetch routine prompt:", err);
    }
  }, []);

  const fetchEmailPrompt = useCallback(async () => {
    try {
      const data = await apiClient.get("/email-prompt");
      setEmailPrompt(data.prompt || "");
      setEmailPromptUpdatedAt(data.updatedAt || "");
      setEmailActiveVersion(data.activeVersion || 0);
      setEmailPromptVersions(data.versions || []);
    } catch (err) {
      console.error("Failed to fetch email prompt:", err);
    }
  }, []);

  const fetchSkills = useCallback(async () => {
    setSkillsLoading(true);
    setSkillsError("");
    try {
      const data = await apiClient.get("/skills");
      setSkills(data.items || []);
    } catch (err) {
      console.error("Failed to fetch skills:", err);
      setSkillsError("Skills could not be loaded. Try refreshing.");
    } finally {
      setSkillsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrompt();
    fetchRoutinePrompt();
    fetchEmailPrompt();
    fetchSkills();
  }, [fetchPrompt, fetchRoutinePrompt, fetchEmailPrompt, fetchSkills]);

  // --- Core Prompt handlers ---
  const handleSavePrompt = async () => {
    setPromptLoading(true);
    setPromptSaved(false);
    try {
      const data = await apiClient.put("/prompt", { prompt: corePrompt });
      setPromptUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || data.version || 0);
      setPromptSaved(true);
      setTimeout(() => setPromptSaved(false), 3000);
      const refreshed = await apiClient.get("/prompt");
      setPromptVersions(refreshed.versions || []);
    } catch (err) {
      console.error("Failed to save prompt:", err);
    } finally {
      setPromptLoading(false);
    }
  };

  const handleActivateVersion = async (version) => {
    if (!window.confirm(`Restore prompt version ${version}?`)) return;
    setPromptLoading(true);
    try {
      await apiClient.put("/prompt", { activateVersion: version });
      await fetchPrompt();
    } catch (err) {
      console.error("Failed to activate version:", err);
    } finally {
      setPromptLoading(false);
    }
  };

  // --- Routine Prompt handlers ---
  const handleSaveRoutinePrompt = async () => {
    setRoutinePromptLoading(true);
    setRoutinePromptSaved(false);
    try {
      const data = await apiClient.put("/routine-prompt", { prompt: routinePrompt });
      setRoutinePromptUpdatedAt(data.updatedAt || "");
      setRoutineActiveVersion(data.activeVersion || data.version || 0);
      setRoutinePromptSaved(true);
      setTimeout(() => setRoutinePromptSaved(false), 3000);
      const refreshed = await apiClient.get("/routine-prompt");
      setRoutinePromptVersions(refreshed.versions || []);
    } catch (err) {
      console.error("Failed to save routine prompt:", err);
    } finally {
      setRoutinePromptLoading(false);
    }
  };

  const handleActivateRoutineVersion = async (version) => {
    if (!window.confirm(`Restore routine prompt version ${version}?`)) return;
    setRoutinePromptLoading(true);
    try {
      await apiClient.put("/routine-prompt", { activateVersion: version });
      await fetchRoutinePrompt();
    } catch (err) {
      console.error("Failed to activate routine version:", err);
    } finally {
      setRoutinePromptLoading(false);
    }
  };

  // --- Email Prompt handlers ---
  const handleSaveEmailPrompt = async () => {
    setEmailPromptLoading(true);
    setEmailPromptSaved(false);
    try {
      const data = await apiClient.put("/email-prompt", { prompt: emailPrompt });
      setEmailPromptUpdatedAt(data.updatedAt || "");
      setEmailActiveVersion(data.activeVersion || data.version || 0);
      setEmailPromptSaved(true);
      setTimeout(() => setEmailPromptSaved(false), 3000);
      const refreshed = await apiClient.get("/email-prompt");
      setEmailPromptVersions(refreshed.versions || []);
    } catch (err) {
      console.error("Failed to save email prompt:", err);
    } finally {
      setEmailPromptLoading(false);
    }
  };

  const handleActivateEmailVersion = async (version) => {
    if (!window.confirm(`Restore email prompt version ${version}?`)) return;
    setEmailPromptLoading(true);
    try {
      await apiClient.put("/email-prompt", { activateVersion: version });
      await fetchEmailPrompt();
    } catch (err) {
      console.error("Failed to activate email version:", err);
    } finally {
      setEmailPromptLoading(false);
    }
  };

  // --- Skills handlers ---
  const handleToggleSkill = async (skill) => {
    setSkillsError("");
    try {
      await apiClient.put(`/skills/${skill.id}`, { active: !skill.active });
      fetchSkills();
    } catch (err) {
      console.error("Toggle failed:", err);
      setSkillsError("The skill status could not be updated.");
    }
  };

  const handleDeleteSkill = async (id) => {
    setSkillsError("");
    try {
      await apiClient.delete(`/skills/${id}`);
      setDeletingSkillId(null);
      fetchSkills();
    } catch (err) {
      console.error("Delete failed:", err);
      setSkillsError("The skill could not be deleted.");
    }
  };

  const handleSkillSaved = () => {
    setEditingSkillId(null);
    setCreatingSkill(false);
    fetchSkills();
  };

  const activeCount = skills.filter((s) => s.active).length;

  return (
    <div className="annacore-page">
      <div className="annacore-header">
        <div>
          <h2>Anna's Core</h2>
          <p className="annacore-subtitle">Manage Anna's system prompt and skill modules</p>
        </div>
      </div>

      <nav className="annacore-tabs" aria-label="Anna Core sections">
        <button
          className={`annacore-tab ${tab === "skills" ? "active" : ""}`}
          onClick={() => switchTab("skills")}
          aria-current={tab === "skills" ? "page" : undefined}
        >
          <span className="annacore-tab-icon" aria-hidden="true">🧩</span>
          <span className="annacore-tab-label">Skills</span>
          <span className="annacore-tab-count">{skills.length}</span>
        </button>
        <button
          className={`annacore-tab ${tab === "routines" ? "active" : ""}`}
          onClick={() => switchTab("routines")}
          aria-current={tab === "routines" ? "page" : undefined}
        >
          <span className="annacore-tab-icon" aria-hidden="true">🤖</span>
          <span className="annacore-tab-label">Routines</span>
        </button>
        <button
          className={`annacore-tab annacore-tab-prompt ${tab === "prompt" ? "active" : ""}`}
          onClick={() => switchTab("prompt")}
          aria-current={tab === "prompt" ? "page" : undefined}
        >
          <span className="annacore-tab-icon" aria-hidden="true">📝</span>
          <span className="annacore-tab-label">Core Prompt</span>
        </button>
        <button
          className={`annacore-tab annacore-tab-prompt ${tab === "routine-prompt" ? "active" : ""}`}
          onClick={() => switchTab("routine-prompt")}
          aria-current={tab === "routine-prompt" ? "page" : undefined}
        >
          <span className="annacore-tab-icon" aria-hidden="true">🔄</span>
          <span className="annacore-tab-label">Routine Prompt</span>
        </button>
        <button
          className={`annacore-tab annacore-tab-prompt ${tab === "email-prompt" ? "active" : ""}`}
          onClick={() => switchTab("email-prompt")}
          aria-current={tab === "email-prompt" ? "page" : undefined}
        >
          <span className="annacore-tab-icon" aria-hidden="true">✉️</span>
          <span className="annacore-tab-label">Email Prompt</span>
        </button>
      </nav>

      {tab === "skills" && (
        <section className="skills-section">
          <div className="skills-intro">
            <h3>Skill Modules</h3>
            <p>Specialized instructions Anna can load into her active context.</p>
          </div>

          <div className="skills-stats">
            <span>{skills.length} skill{skills.length !== 1 ? "s" : ""}</span>
            <span className="skills-stat-sep">·</span>
            <span className="skills-active-count">{activeCount} active</span>
            <div className="skills-stats-actions">
              <RefreshButton onClick={fetchSkills} title="Refresh skills" />
              {!creatingSkill && (
                <button
                  className="icon-btn icon-btn-add"
                  onClick={() => { setCreatingSkill(true); setEditingSkillId(null); setDeletingSkillId(null); }}
                  title="New Skill"
                  aria-label="New Skill"
                >
                  +
                </button>
              )}
            </div>
          </div>

          {skillsError && <div className="skills-error">{skillsError}</div>}

          {creatingSkill && (
            <SkillForm
              item={null}
              onSave={handleSkillSaved}
              onCancel={() => setCreatingSkill(false)}
            />
          )}

          {skillsLoading ? (
            <div className="placeholder">Loading...</div>
          ) : skills.length === 0 ? (
            <div className="placeholder">
              No skills yet. Create your first skill to extend Anna's capabilities!
            </div>
          ) : (
            <div className="skills-list">
              {skills.map((skill) =>
                editingSkillId === skill.id ? (
                  <SkillForm
                    key={skill.id}
                    item={skill}
                    onSave={handleSkillSaved}
                    onCancel={() => setEditingSkillId(null)}
                  />
                ) : (
                  <article key={skill.id} className={`skill-card ${skill.active ? "skill-active" : ""}`}>
                    <div className="skill-top">
                      <div className="skill-info">
                        <span className="skill-name">{skill.name}</span>
                        {skill.description && (
                          <span className="skill-desc">{skill.description}</span>
                        )}
                      </div>
                      <div className="skill-toggle-wrap">
                        <label className="skill-toggle">
                          <input
                            type="checkbox"
                            checked={!!skill.active}
                            onChange={() => handleToggleSkill(skill)}
                            aria-label={`${skill.active ? "Unload" : "Load"} ${skill.name}`}
                          />
                          <span className="skill-toggle-slider" />
                        </label>
                        <span className={`skill-status ${skill.active ? "loaded" : "unloaded"}`}>
                          {skill.active ? "Loaded" : "Unloaded"}
                        </span>
                      </div>
                    </div>
                    {deletingSkillId === skill.id && (
                      <div className="instruction-confirm-bar">
                        <span>Delete this skill? This cannot be undone.</span>
                        <button className="btn-card-action btn-card-danger" onClick={() => handleDeleteSkill(skill.id)}>Delete</button>
                        <button className="btn-card-action" onClick={() => setDeletingSkillId(null)}>Cancel</button>
                      </div>
                    )}
                    <div className="skill-actions">
                      <span className="skill-date">
                        {skill.updatedAt ? `Updated ${new Date(skill.updatedAt).toLocaleDateString()}` : ""}
                      </span>
                      <button className="btn-card-action" onClick={() => { setEditingSkillId(skill.id); setCreatingSkill(false); setDeletingSkillId(null); }}>Edit</button>
                      <button className="btn-card-action btn-card-danger" onClick={() => setDeletingSkillId(skill.id)}>Delete</button>
                    </div>
                  </article>
                )
              )}
            </div>
          )}
        </section>
      )}

      {tab === "prompt" && (
        <div className="core-prompt-section">
          <p className="prompt-description">
            This is Anna's core system prompt. Changes take effect immediately on the next message.
            {activeVersion > 0 && <span className="prompt-version-badge">v{activeVersion}</span>}
          </p>
          <div className="prompt-token-bar">
            <span>{corePrompt.length.toLocaleString()} chars</span>
            <span className="prompt-token-sep">·</span>
            <span>~{estimateTokens(corePrompt).toLocaleString()} tokens</span>
          </div>
          <textarea
            className="prompt-editor"
            value={corePrompt}
            onChange={(e) => setCorePrompt(e.target.value)}
            placeholder="Enter the core system prompt for Anna..."
            spellCheck={false}
          />
          <div className="prompt-actions">
            <button
              className="btn-primary"
              onClick={handleSavePrompt}
              disabled={promptLoading}
            >
              {promptLoading ? "Saving..." : "Save as New Version"}
            </button>
            {promptSaved && <span className="save-confirm">✓ Saved as v{activeVersion}</span>}
            {promptUpdatedAt && (
              <span className="prompt-updated">
                Last updated: {new Date(promptUpdatedAt).toLocaleString()}
              </span>
            )}
          </div>

          {promptVersions.length > 0 && (
            <div className="prompt-versions">
              <h3>Version History</h3>
              <div className="versions-list">
                {promptVersions.map((v) => (
                  <div
                    key={v.version}
                    className={`version-item ${v.version === activeVersion ? "active" : ""}`}
                  >
                    <div className="version-info">
                      <span className="version-number">v{v.version}</span>
                      {v.version === activeVersion && <span className="version-active-tag">Active</span>}
                      <span className="version-date">
                        {v.createdAt ? new Date(v.createdAt).toLocaleString() : "—"}
                      </span>
                      <span className="version-chars">{v.charCount?.toLocaleString()} chars · ~{Math.ceil((v.charCount || 0) / 4).toLocaleString()} tokens</span>
                    </div>
                    <div className="version-preview">{v.preview}</div>
                    {v.version !== activeVersion && (
                      <button
                        className="btn-restore"
                        onClick={() => handleActivateVersion(v.version)}
                        disabled={promptLoading}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "routines" && <Routines />}

      {tab === "routine-prompt" && (
        <div className="core-prompt-section">
          <p className="prompt-description">
            This is Anna's routine system prompt — used when executing scheduled routines. Changes take effect on the next routine run.
            {routineActiveVersion > 0 && <span className="prompt-version-badge">v{routineActiveVersion}</span>}
          </p>
          <div className="prompt-token-bar">
            <span>{routinePrompt.length.toLocaleString()} chars</span>
            <span className="prompt-token-sep">·</span>
            <span>~{estimateTokens(routinePrompt).toLocaleString()} tokens</span>
          </div>
          <textarea
            className="prompt-editor"
            value={routinePrompt}
            onChange={(e) => setRoutinePrompt(e.target.value)}
            placeholder="Enter the routine system prompt for Anna..."
            spellCheck={false}
          />
          <div className="prompt-actions">
            <button
              className="btn-primary"
              onClick={handleSaveRoutinePrompt}
              disabled={routinePromptLoading}
            >
              {routinePromptLoading ? "Saving..." : "Save as New Version"}
            </button>
            {routinePromptSaved && <span className="save-confirm">✓ Saved as v{routineActiveVersion}</span>}
            {routinePromptUpdatedAt && (
              <span className="prompt-updated">
                Last updated: {new Date(routinePromptUpdatedAt).toLocaleString()}
              </span>
            )}
          </div>

          {routinePromptVersions.length > 0 && (
            <div className="prompt-versions">
              <h3>Version History</h3>
              <div className="versions-list">
                {routinePromptVersions.map((v) => (
                  <div
                    key={v.version}
                    className={`version-item ${v.version === routineActiveVersion ? "active" : ""}`}
                  >
                    <div className="version-info">
                      <span className="version-number">v{v.version}</span>
                      {v.version === routineActiveVersion && <span className="version-active-tag">Active</span>}
                      <span className="version-date">
                        {v.createdAt ? new Date(v.createdAt).toLocaleString() : "—"}
                      </span>
                      <span className="version-chars">{v.charCount?.toLocaleString()} chars · ~{Math.ceil((v.charCount || 0) / 4).toLocaleString()} tokens</span>
                    </div>
                    <div className="version-preview">{v.preview}</div>
                    {v.version !== routineActiveVersion && (
                      <button
                        className="btn-restore"
                        onClick={() => handleActivateRoutineVersion(v.version)}
                        disabled={routinePromptLoading}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "email-prompt" && (
        <div className="core-prompt-section">
          <p className="prompt-description">
            System prompt the gmail-sync lambda sends to Anna-Gemma when summarising new inbox messages.
            Runs on local Gemma (gemma4:26b-a4b-it-q8_0). Changes take effect on the next gmail_sync cold start (~15 min).
            {emailActiveVersion > 0 && <span className="prompt-version-badge">v{emailActiveVersion}</span>}
          </p>
          <div className="prompt-token-bar">
            <span>{emailPrompt.length.toLocaleString()} chars</span>
            <span className="prompt-token-sep">·</span>
            <span>~{estimateTokens(emailPrompt).toLocaleString()} tokens</span>
          </div>
          <textarea
            className="prompt-editor"
            value={emailPrompt}
            onChange={(e) => setEmailPrompt(e.target.value)}
            placeholder="Enter the email-triage system prompt for Anna..."
            spellCheck={false}
          />
          <div className="prompt-actions">
            <button
              className="btn-primary"
              onClick={handleSaveEmailPrompt}
              disabled={emailPromptLoading}
            >
              {emailPromptLoading ? "Saving..." : "Save as New Version"}
            </button>
            {emailPromptSaved && <span className="save-confirm">✓ Saved as v{emailActiveVersion}</span>}
            {emailPromptUpdatedAt && (
              <span className="prompt-updated">
                Last updated: {new Date(emailPromptUpdatedAt).toLocaleString()}
              </span>
            )}
          </div>

          {emailPromptVersions.length > 0 && (
            <div className="prompt-versions">
              <h3>Version History</h3>
              <div className="versions-list">
                {emailPromptVersions.map((v) => (
                  <div
                    key={v.version}
                    className={`version-item ${v.version === emailActiveVersion ? "active" : ""}`}
                  >
                    <div className="version-info">
                      <span className="version-number">v{v.version}</span>
                      {v.version === emailActiveVersion && <span className="version-active-tag">Active</span>}
                      <span className="version-date">
                        {v.createdAt ? new Date(v.createdAt).toLocaleString() : "—"}
                      </span>
                      <span className="version-chars">{v.charCount?.toLocaleString()} chars · ~{Math.ceil((v.charCount || 0) / 4).toLocaleString()} tokens</span>
                    </div>
                    <div className="version-preview">{v.preview}</div>
                    {v.version !== emailActiveVersion && (
                      <button
                        className="btn-restore"
                        onClick={() => handleActivateEmailVersion(v.version)}
                        disabled={emailPromptLoading}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SkillForm({ item, onSave, onCancel }) {
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [prompt, setPrompt] = useState(item?.prompt || "");
  const [active, setActive] = useState(item?.active ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = e.target.scrollHeight + "px";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !prompt.trim()) return;
    setSaving(true);
    setError("");
    try {
      const body = { name: name.trim(), description: description.trim(), prompt: prompt.trim(), active };
      if (item?.id) {
        await apiClient.put(`/skills/${item.id}`, body);
      } else {
        await apiClient.post("/skills", body);
      }
      onSave();
    } catch (err) {
      console.error("Save failed:", err);
      setError(err.message || "The skill could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="skill-form-overlay" ref={formRef}>
      <form className="skill-form" onSubmit={handleSubmit}>
        <h3>{item ? "Edit Skill" : "New Skill"}</h3>
        {error && <div className="skill-form-error">{error}</div>}

        <label>
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Spotify Control"
            required
          />
        </label>

        <label>
          Description
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of what this skill enables"
          />
        </label>

        <label>
          Prompt
          <textarea
            ref={textareaRef}
            className="auto-textarea"
            value={prompt}
            onChange={handlePromptChange}
            placeholder="The skill prompt that will be injected into Anna's system prompt when loaded..."
            rows={1}
            required
          />
        </label>

        <label className="skill-form-toggle">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Load skill immediately
        </label>

        <div className="skill-form-actions">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving || !name.trim() || !prompt.trim()}>
            {saving ? "Saving..." : item ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AnnaCore;
